import React, { useState, useEffect, useRef } from "react"
import { X, Send } from "lucide-react"

const ChatSidebar = ({
  isOpen,
  onClose,
  meetingId,
  userId,
  userRole = "interviewer",
  orgId = "",
  aiSuggestions = [],
  autoOpenSuggestions = true,
  onToggleAutoOpen = () => {},
}) => {
  const [messages, setMessages] = useState([
    {
      text: "Hey! I'm your AI assistant. I'll give you suggestions during the interview or answer any questions you have.",
      sender: "ai",
      type: "greeting",
      id: Date.now(),
      timestamp: new Date(),
    },
  ])
  const [userInput, setUserInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
  }, [isOpen])

  useEffect(() => {
    if (aiSuggestions.length > 0) {
      const latestSuggestion = aiSuggestions[aiSuggestions.length - 1]

      // Check if this suggestion is already in messages
      const alreadyExists = messages.some(
        (msg) => msg.id === latestSuggestion.id
      )

      if (!alreadyExists) {
        setMessages((prev) => [...prev, latestSuggestion])
      }
    }
  }, [aiSuggestions])

  const handleUserQuestion = async (question) => {
    if (!question.trim()) return

    // Add user message
    const userMessage = {
      text: question,
      sender: "user",
      type: "question",
      id: Date.now(),
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, userMessage])
    setUserInput("")
    setIsLoading(true)

    try {
      const response = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/interviews/ai/assistant`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: question,
            interviewId: meetingId,
            meetingId: meetingId,
            orgId: orgId,
            userId: userId,
            userRole: userRole,
          }),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      const aiMessage = {
        text:
          result.data?.answer ||
          result.answer ||
          "I couldn't generate a response. Please try rephrasing your question.",
        sender: "ai",
        type: "response",
        id: Date.now() + 1,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, aiMessage])
    } catch (error) {
      console.error("Failed to get AI response:", error)

      const errorMessage = {
        text: "Sorry, I'm having trouble connecting right now. Please try again in a moment.",
        sender: "ai",
        type: "response",
        id: Date.now() + 1,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleUserQuestion(userInput)
  }

  // Don't render if not open
  if (!isOpen) {
    return null
  }

  return (
    <div className="h-full w-[25%] flex flex-col font-satoshi bg-[#1a1a1a] rounded-2xl transition-all duration-300 ease-in-out ml-2 mt-2 mr-2">
      {/* Header */}
      <div className="px-6 py-4 flex justify-between items-center flex-shrink-0">
        <h3 className="text-lg font-normal text-white">In-call Assistant</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          title="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#374151 transparent",
        }}
      >
        {/* Auto-open suggestions toggle */}
        <div className="bg-[#252525] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-gray-300 text-sm">
            {autoOpenSuggestions
              ? "Suggestions open automatically"
              : "Open suggestions manually"}
          </span>
          <button
            onClick={onToggleAutoOpen}
            className={`relative w-11 h-6 rounded-full flex items-center transition-colors ${
              autoOpenSuggestions ? "bg-blue-600" : "bg-[#4a5056]"
            }`}
          >
            <div
              className={`absolute w-5 h-5 rounded-full bg-[#e8eaed] shadow-sm flex items-center justify-center transition-all ${
                autoOpenSuggestions ? "right-0.5" : "left-0.5"
              }`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#3c4043]"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </button>
        </div>

        {/* Continuous chat is OFF card */}
        <div className="bg-[#252525] rounded-xl px-3 py-4 text-center">
          <div className="flex items-center justify-center gap-2.5 mb-1">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="text-gray-200 font-normal text-sm">
              Continuous chat is OFF
            </span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed text-[13px]">
            Messages won't be saved when the call ends. You can pin a message to
            make it visible for people who join later.
          </p>
        </div>

        {(() => {
          // Find the latest suggestion message
          const latestSuggestionId = messages
            .filter((msg) => msg.type === "suggestion")
            .slice(-1)[0]?.id

          return messages.map((message) => {
            const isLatestSuggestion =
              message.type === "suggestion" && message.id === latestSuggestionId

            return (
              <div key={message.id} className="flex flex-col">
                {message.sender === "user" ? (
                  // User message
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-500 mb-1.5">You</span>
                    <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-3 bg-blue-600 text-white">
                      <p className="text-sm leading-relaxed">{message.text}</p>
                    </div>
                    <span className="text-xs text-gray-600 mt-1.5">
                      {message.timestamp.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ) : (
                  // AI message
                  <div className="flex gap-3">
                    {/* AI Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-7 h-7 rounded-full bg-[#004a77] flex items-center justify-center text-white text-[13px] font-medium">
                        A
                      </div>
                    </div>

                    {/* Message content */}
                    <div className="flex-1 flex flex-col">
                      {/* Different styling for suggestions vs regular messages */}
                      {message.type === "suggestion" ? (
                        // Suggestion Card Style with highlight for latest
                        <div
                          className={`max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3 transition-all ${
                            isLatestSuggestion
                              ? "bg-[#2a3f5f] border-2 border-blue-500/50 shadow-lg shadow-blue-500/20"
                              : "bg-[#252525]"
                          }`}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="flex-shrink-0 mt-0.5">
                              <span className="text-base">💡</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm leading-relaxed text-gray-300">
                                {message.text}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Regular Response Style
                        <div className="max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3 bg-[#252525]">
                          <p className="text-sm leading-relaxed text-gray-200">
                            {message.text}
                          </p>
                        </div>
                      )}

                      <span className="text-xs text-gray-600 mt-1.5">
                        {message.timestamp.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )
          })
        })()}

        {isLoading && (
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium">
                H
              </div>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3 bg-[#252525]">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                  <div
                    className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.15s" }}
                  />
                  <div
                    className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.3s" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 flex-shrink-0">
        <form onSubmit={handleSubmit} className="relative">
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="Send a message"
            className="w-full bg-[#1a1a1a] text-gray-100 placeholder-gray-500 rounded-full pl-5 pr-12 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-600 border-2 border-gray-700 transition-all"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!userInput.trim() || isLoading}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-gray-400 hover:text-blue-500 hover:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  )
}

export default ChatSidebar